import { useCallback, useEffect, useState } from "react";
import { mcpCall, consumerIdOrThrow } from "../lib/mcp-client";

// "What do you know about me?" — surfaces the consumer's Aristotle
// belief graph + contradiction queue (LS-SPEC-10 §Consumer Visibility).
// Belief items show live decayed confidence; tapping Forget removes
// the belief (LS-SPEC-10 §Forgetting Rights).

interface BeliefRecord {
  belief: {
    belief_id: string;
    category: string;
    key: string;
    value: unknown;
    base_confidence: number;
    reinforcement_count: number;
    source_episode_ids: string[];
  };
  live_confidence: number;
}


export function TwinAboutMe() {
  const [beliefs, setBeliefs] = useState<BeliefRecord[] | null>(null);
  const [contradictions, setContradictions] = useState<BeliefRecord[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const id = consumerIdOrThrow();
      const [b, c] = await Promise.all([
        mcpCall<{ beliefs: BeliefRecord[] }>(
          "aristotle.beliefs", { consumer_id: id },
        ),
        mcpCall<{ contradictions: BeliefRecord[] }>(
          "aristotle.contradictions", { consumer_id: id },
        ),
      ]);
      setBeliefs(b.beliefs || []);
      setContradictions(c.contradictions || []);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const forget = async (belief_id: string) => {
    try {
      await mcpCall("aristotle.forget", { belief_id });
      load();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const byCategory = (beliefs || []).reduce<Record<string, BeliefRecord[]>>(
    (acc, r) => {
      const cat = r.belief.category;
      (acc[cat] ||= []).push(r);
      return acc;
    },
    {},
  );

  return (
    <div className="flex flex-col gap-3 px-4 py-3">
      <header>
        <h1 className="text-base font-medium text-(--color-ink)">
          What I know about you
        </h1>
        <p className="text-xs text-(--color-ink-muted)">
          Every belief came from something you said, booked, or rated.
          You can forget any of them.
        </p>
      </header>

      {err && (
        <p className="rounded-md bg-red-900/40 px-2 py-1 text-xs text-red-200">
          {err}
        </p>
      )}

      {contradictions.length > 0 && (
        <section className="rounded-xl border border-amber-500/30 bg-(--color-canvas-card) p-3">
          <h2 className="mb-1 text-xs uppercase tracking-wide text-amber-300">
            We noticed a mix-up
          </h2>
          <p className="mb-2 text-xs text-(--color-ink-muted)">
            Two things you've told me disagree. Pick the one that's right
            and I'll forget the other.
          </p>
          <ul className="space-y-1.5">
            {contradictions.map((c) => (
              <ContradictionRow key={c.belief.belief_id} record={c}
                                onResolve={load}
                                allContradictions={contradictions} />
            ))}
          </ul>
        </section>
      )}

      {Object.keys(byCategory).length === 0 ? (
        <p className="text-xs text-(--color-ink-faint)">
          Nothing yet — beliefs land as you talk to me and accept deals.
        </p>
      ) : (
        Object.entries(byCategory).map(([cat, items]) => (
          <section key={cat}
                   className="rounded-xl border border-white/5 bg-(--color-canvas-card) p-3">
            <h2 className="mb-2 text-xs uppercase tracking-wide text-(--color-ink-faint)">
              {cat.replace("_", " ")}
            </h2>
            <ul className="space-y-1.5">
              {items.map((r) => (
                <BeliefRow key={r.belief.belief_id} record={r}
                           onForget={forget} />
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}


function BeliefRow({
  record, onForget,
}: { record: BeliefRecord; onForget: (id: string) => void }) {
  const pct = Math.round(record.live_confidence * 100);
  return (
    <li className="flex items-baseline gap-2 text-xs">
      <span className="flex-1 truncate text-(--color-ink-muted)">
        <span className="text-(--color-ink)">{record.belief.key}</span>:{" "}
        <span className="font-mono">{String(record.belief.value)}</span>
      </span>
      <span className="h-1 w-10 bg-(--color-canvas-elev) rounded-full overflow-hidden">
        <span className="block h-full bg-(--color-accent-gold)"
              style={{ width: `${pct}%` }} />
      </span>
      <span className="tabular-nums text-(--color-ink-faint) w-7 text-right">
        {pct}
      </span>
      <button
        onClick={() => onForget(record.belief.belief_id)}
        className="rounded-(--radius-pill) px-2 py-0.5 text-(--color-ink-faint) hover:text-red-300"
      >
        Forget
      </button>
    </li>
  );
}


function ContradictionRow({
  record, onResolve, allContradictions,
}: {
  record: BeliefRecord;
  onResolve: () => void;
  allContradictions: BeliefRecord[];
}) {
  // Find the OTHER contradicting beliefs in the same (category, key)
  // bucket so we can mark them superseded when the consumer picks one.
  const others = allContradictions.filter(
    (c) =>
      c.belief.belief_id !== record.belief.belief_id &&
      c.belief.category === record.belief.category &&
      c.belief.key === record.belief.key,
  );
  const keep = async () => {
    try {
      for (const loser of others) {
        await mcpCall("aristotle.resolve_contradiction", {
          winning_belief_id: record.belief.belief_id,
          losing_belief_id: loser.belief.belief_id,
        });
      }
      onResolve();
    } catch (e) {
      alert((e as Error).message);
    }
  };
  return (
    <li className="flex items-baseline gap-2 text-xs">
      <span className="flex-1 truncate text-(--color-ink-muted)">
        <span className="font-mono">
          {record.belief.key}={String(record.belief.value)}
        </span>
      </span>
      <button
        onClick={keep}
        className="rounded-(--radius-pill) bg-(--color-accent-gold) px-2 py-0.5 text-stone-900"
      >
        Keep this one
      </button>
    </li>
  );
}
